import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import importAll from "./images";
import Button from "../form-components/Button";
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTone } from "../../features/tones/getToneSlice";
import { debounce } from "../../utils/utils";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";

const images = importAll(
  require.context(
    "../../assets/images/",
    false,
    /\.(png|jpe?g|svg|GIF|webp|json)$/
  )
);

const ToneFooter = ({ userId, setSelectedTones, UpdateData, basePrompt }) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const { tones, isLoading } = useSelector((state) => state?.getToneReducer);
  const [selectedTone, setSelectedTone] = useState({});
  const [tonesList, setTonesList] = useState([]);
  const [isListEmpty, setIsListEmpty] = useState(false);
  const tonesRef = useRef(null);
  const { particularConversationData } = useSelector(
    (state) => state.getParticularConversationData
  );
  const toneUUID = particularConversationData?.settings?.toneUUID;

  const getToneFunction = async () => {
    dispatch(
      getTone({ perPage, page: currentPage, search, toneUUID: toneUUID })
    );
  };

  const debounceSearch = debounce((e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  }, 600);

  const scrollToBottom = (refEl) => {
    if (refEl.current) {
      setTimeout(() => {
        const { scrollHeight } = refEl.current;
        refEl.current.scrollTop = scrollHeight;
      }, 200);
    }
  };

  useEffect(() => {
    setSelectedTone({});
    setTonesList([]);

    if (!tones || tones.length === 0) {
      getToneFunction();
    }
  }, []);

  useEffect(() => {
    getToneFunction();
  }, [search, currentPage]);

  useEffect(() => {
    if (search === "" && currentPage === 1) {
      getToneFunction();
    } else {
      setSearch("");
      setCurrentPage(1);
    }
  }, [particularConversationData]);

  useEffect(() => {
    if (currentPage === 1) {
      setTonesList([...tones]);
    } else {
      setTonesList([...tonesList, ...tones]);
    }
    setIsListEmpty(!Boolean(tones.length) || tones.length < perPage);
  }, [tones, toneUUID]);

  useEffect(() => {
    if (tonesList && tonesList.length > 0) {
      const defaultTone =
        tonesList.find((tone) => tone.uuid === toneUUID || tone.is_default) ||
        tonesList[0];
      setSelectedTone({ ...defaultTone });
    }
  }, [tonesList, toneUUID]);

  useEffect(() => {
    if (selectedTone.uuid) {
      setSelectedTones({ ...selectedTone });
    }
  }, [selectedTone]);

  useLayoutEffect(() => {
    if (tonesRef?.current && currentPage !== 1) {
      scrollToBottom(tonesRef);
    }
  }, [tonesList]);

  const selectTone = (tone) => {
    setSelectedTone({ ...tone });
    document.body.click();
    UpdateData(basePrompt.uuid, tone.uuid);
  };

  const ToneSelectDropdown = forwardRef((props, ref) => {
    useInfiniteScroll(() => {
      if (!isListEmpty) {
        setCurrentPage((page) => page + 1);
      }
    }, ref);

    return (
      <div>
        <div className="filtertags-dropdown">
          <div className="intro-searchbar-main pt-1 mt-1">
            <div className="intro-searchbar">
              <button
                disabled=""
                type="submit"
                className="filter-search-btn"
              ></button>
              <input
                type="text"
                className="form-control ft-14"
                name="search"
                placeholder="Search"
                defaultValue={search}
                onChange={(e) => {
                  e.persist();
                  debounceSearch(e);
                }}
                autoFocus={Boolean(search)}
              />
            </div>
          </div>
          <div className="px-1 pb-2">
            <div
              className="cmn-list-prsona persona-cmn-list"
              ref={(r) => (ref.current = r)}
            >
              <ul>
                {tonesList.length === 0 ? (
                  <li>
                    <div className="cmn-fl-label">No tones found.</div>
                  </li>
                ) : (
                  tonesList.map((tone) => (
                    <li key={tone.uuid} onClick={() => selectTone(tone)}>
                      <div
                        className={`cmn-fl-label ${
                          selectedTone?.uuid === tone.uuid ? "active" : ""
                        }`}
                      >
                        <p className="mb-0">
                          {tone.name.slice(0, 25)}
                          {tone.name.length > 25 ? "..." : ""}
                        </p>
                        <OverlayTrigger
                          placement="right"
                          delay={{ show: 250, hide: 400 }}
                          overlay={
                            <Tooltip
                              className="persona-drop-tooltip"
                              id="button-tooltip-2"
                            >
                              <div className="d-flex pf-tooltip-div">
                                <h4 className="mb-0">{tone.name}</h4>
                              </div>
                              <p className="mb-0">{tone.description}</p>
                            </Tooltip>
                          }
                        >
                          <div className="prs-list-info">
                            <img src={images["info.svg"]} alt="info" />
                          </div>
                        </OverlayTrigger>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="text-center p-1 pb-2 custom-loader-riv">
            <div className="custom-loader"></div>
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="d-flex align-items-center flex-nowrap sm:flex-wrap">
      <h4>
        <img className="" src={images["icn_tones.svg"]} alt="Tone" />
        Tones:
      </h4>
      <div className="persona-react-select">
        <OverlayTrigger
          trigger="click"
          placement="top"
          overlay={(props) => (
            <Popover
              className="filter-chat-popover"
              id="popover-basic"
              {...props}
            >
              <Popover.Body>
                <ToneSelectDropdown ref={tonesRef} />
              </Popover.Body>
            </Popover>
          )}
          rootClose
        >
          <span>
            <Button className="btn p-0 bg-transparent border-0 persona-fl-btn">
              <p className="mb-0">
                {selectedTone?.name?.slice(0, 25) ?? "Select Tone"}
                {selectedTone?.name?.length > 25 ? "..." : ""}
              </p>
              <img
                className="w-100"
                src={images["dropdown-arrow.svg"]}
                alt="dropdown-arrow"
              />
            </Button>
          </span>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default ToneFooter;
